import { useMemo } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { dispatch } from "../../../../state/store";
import { setIsFiltersPanelVisible } from "../../../../state/ducks";

import { colors, strCode } from "../../../../consts";
import { t } from "../../../../services";
import { useDashboardFilterOptions, useSelectedDashboardFilters } from "../../../../hooks";

import {
  Button,
  FlexContainers,
  Heading6,
  IconButton,
  Modal,
  SVG,
  TextButton,
  WrapperContainer
} from "../../../../components";

import { ToggleFiltersButton } from "../styled";
import { FiltersPanelContainer } from "./styled";
import FiltersList from "./FiltersList";

const FiltersPanel = ({
  isLoading,
  isMarketAdminOnly,

  onUpdateDevice,
  onUpdateMarket,
  onUpdateLocation,
  onUpdatePlatform,
  onUpdateBrowser,
  onUpdateDateRange,
  onApplyFilters,
  onResetFilters,
  onUpdateAppType,
  onUpdateGroup,
  onUpdateConnectivity
}) => {
  const isFiltersPanelVisible = useSelector((store) => store.rum.isFiltersPanelVisible);
  const isFiltersChanged = useSelector((store) => store.dashboardFilters.isFiltersChanged);

  const { locationOptions } = useDashboardFilterOptions();

  const {
    selectedLocation,
    selectedMarkets,
    selectedPlatform,
    selectedBrowser,
    selectedAppType,
    selectedGroup,
    selectedConnectivity
  } = useSelectedDashboardFilters();

  const isApplyButtonDisabled = useMemo(() => {
    return (
      (selectedMarkets && !selectedMarkets?.length) ||
      (selectedLocation &&
        selectedMarkets &&
        !selectedMarkets.some((market) => market.id === 0) &&
        !selectedLocation?.length &&
        locationOptions.length !== 0) ||
      (selectedPlatform && !selectedPlatform?.length) ||
      (selectedBrowser && !selectedBrowser?.length) ||
      (selectedAppType && !selectedAppType?.length) ||
      (selectedGroup && !selectedGroup?.length) ||
      (selectedConnectivity && !selectedConnectivity?.length)
    );
  }, [
    selectedMarkets,
    selectedLocation,
    selectedPlatform,
    selectedBrowser,
    selectedAppType,
    selectedGroup,
    selectedConnectivity
  ]);

  const modalFooter = (
    <WrapperContainer Container={FlexContainers.FlexColumn} width={298}>
      <WrapperContainer height={40} mb={20} width="100%">
        <Button
          disabled={isLoading || !isFiltersChanged || isApplyButtonDisabled}
          onClick={() => onApplyFilters()}>
          {t(strCode.APPLY_BTN)}
        </Button>
      </WrapperContainer>

      <WrapperContainer height={40} width="100%" mb={-10} Container={FlexContainers.FlexRowCenter}>
        <TextButton disabled={isLoading} onClick={() => onResetFilters()}>
          {t(strCode.RESET_TO_DEFAULT)}
        </TextButton>
      </WrapperContainer>
    </WrapperContainer>
  );

  const modalHeader = (
    <WrapperContainer Container={FlexContainers.FlexRowBetween} width="100%" mb={23}>
      <Heading6>{t(strCode.FILTER_TITLE)}</Heading6>
      <IconButton onClick={() => dispatch(setIsFiltersPanelVisible(!isFiltersPanelVisible))}>
        <SVG.CloseIcon />
      </IconButton>
    </WrapperContainer>
  );

  return (
    <FiltersPanelContainer>
      <WrapperContainer Container={FlexContainers.FlexRowStart} gap={10}>
        <ToggleFiltersButton
          secondary={!isFiltersPanelVisible}
          onClick={() => dispatch(setIsFiltersPanelVisible(!isFiltersPanelVisible))}>
          <SVG.DashboardFilterIcon />
        </ToggleFiltersButton>

        <Modal
          id="dashboard-filters-modal"
          active={isFiltersPanelVisible}
          isFullHeight
          alignRight
          preventOverflow
          preventContentScroll
          backgroundColor={colors.ISABELLINE}
          modalHeader={modalHeader}
          closable={false}
          modalFooter={modalFooter}
          setActive={() => dispatch(setIsFiltersPanelVisible(false))}
          closeOnBackgroundClick>
          <FiltersList
            isInlineView={true}
            isLoading={isLoading}
            isMarketAdminOnly={isMarketAdminOnly}
            onUpdateDateRange={onUpdateDateRange}
            onUpdateMarket={onUpdateMarket}
            onUpdateBrowser={onUpdateBrowser}
            onUpdateLocation={onUpdateLocation}
            onUpdateDevice={onUpdateDevice}
            onUpdatePlatform={onUpdatePlatform}
            onUpdateAppType={onUpdateAppType}
            onUpdateGroup={onUpdateGroup}
            onUpdateConnectivity={onUpdateConnectivity}
          />
        </Modal>
      </WrapperContainer>
    </FiltersPanelContainer>
  );
};

FiltersPanel.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  isMarketAdminOnly: PropTypes.bool.isRequired,

  onUpdateDevice: PropTypes.func.isRequired,
  onUpdateMarket: PropTypes.func.isRequired,
  onUpdateLocation: PropTypes.func.isRequired,
  onUpdatePlatform: PropTypes.func.isRequired,
  onUpdateBrowser: PropTypes.func.isRequired,
  onUpdateDateRange: PropTypes.func.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  onUpdateAppType: PropTypes.func.isRequired,
  onUpdateGroup: PropTypes.func.isRequired,
  onUpdateConnectivity: PropTypes.func.isRequired
};

export default FiltersPanel;
